import React from "react";
import {
  bandFor,
  criterionLabel,
  parseScore,
  scoreText,
} from "../config/rubric";
import "./QuestionRubric.css";

// One question's rubric, in two forms.
//
// Presentational only - no handlers, no fetches - which is why they live here
// rather than inside the review page: the page owns the reviewer's actions, and
// these own how a verdict looks.

// The scale as segments rather than a continuous bar. The scores underneath are
// small integers, and a smooth bar would imply a precision they do not have.
const ScoreMeter = ({ score }) => {
  if (!score) return null;
  const segments = Number.isInteger(score.max) && score.max <= 6 ? score.max : 1;
  return (
    <span className={`qr-meter qr-meter-${bandFor(score.ratio)}`} aria-hidden="true">
      {Array.from({ length: segments }, (_, index) => (
        <i key={index} className={index < score.value ? "is-filled" : ""} />
      ))}
    </span>
  );
};

// The seven criteria as one compact list: the name, how far along the scale it
// landed, and the mark. This is what a reviewer reads first - the SHAPE of a
// question's evaluation - and it fits on a screen, which seven cards of prose
// does not. The reasoning behind each mark is a click away, below.
export const QuestionRubricSummary = ({ assessment }) => {
  const criteria = Object.entries(assessment || {});
  if (criteria.length === 0) return null;
  return (
    <ul className="qr-rubric-list">
      {criteria.map(([name, verdict]) => {
        const score = parseScore(verdict);
        const skipped = Boolean(verdict?.skipped);
        // Asked and unanswerable, which is neither a mark nor a skip: it still
        // counts toward the maximum, so it costs the question marks.
        const failed = !skipped && !score;
        const band = skipped || failed ? "unknown" : bandFor(score.ratio);
        return (
          <li
            className={`qr-rubric-row is-${band}${skipped ? " is-skipped" : ""}${
              failed ? " is-failed" : ""}`}
            key={name}
          >
            <span className="qr-rubric-name">
              {verdict?.name || criterionLabel(name)}
            </span>
            <span className="qr-rubric-bar" aria-hidden="true">
              {score && <i style={{ width: `${score.ratio * 100}%` }} />}
            </span>
            <span className="qr-rubric-mark">
              {skipped ? "not assessed" : failed ? "unscored" : scoreText(verdict, score)}
              {verdict?.label && !skipped && !failed && <em>{verdict.label}</em>}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export const QuestionRubric = ({ assessment }) => {
  const criteria = Object.entries(assessment || {});
  if (criteria.length === 0) {
    return <p className="qr-empty">No rubric assessment recorded.</p>;
  }
  return (
    <div className="qr-criteria-grid">
      {criteria.map(([name, verdict]) => {
        const score = parseScore(verdict);
        // A skipped criterion was never asked, because the course outcomes it
        // depends on are unknown. It is not a zero and must not read as one: it
        // leaves the denominator entirely, so a question with one skipped is
        // scored out of 24 rather than 28. Shown greyed with the stated reason
        // rather than hidden, because "we did not assess this, here is why" is
        // information a reviewer needs.
        const skipped = Boolean(verdict?.skipped);
        // Asked, and answered with nothing usable. A third state, and it needs
        // its own treatment because it behaves like neither of the others: it
        // still counts toward the maximum, so it costs the question marks
        // exactly as a real zero would, for a reason that has nothing to do
        // with the paper. Grey would say "not counted", which is false.
        const failed = !skipped && !score;
        const band = skipped ? "unknown" : failed ? "unknown" : bandFor(score.ratio);
        const issues = Array.isArray(verdict?.issues) ? verdict.issues : [];
        return (
          <div
            className={`qr-criterion-card is-${band}${skipped ? " is-skipped" : ""}${
              failed ? " is-failed" : ""}`}
            key={name}
          >
            <div className="qr-criterion-top">
              <span className="qr-criterion-name">
                {verdict?.name || criterionLabel(name)}
              </span>
              <span className="qr-criterion-pill">
                {failed ? "unscored" : scoreText(verdict, score)}
              </span>
            </div>
            {!skipped && !failed && <ScoreMeter score={score} />}
            <p className="qr-criterion-just">
              {skipped
                ? verdict?.reason || "Not assessed."
                : failed
                  ? "The model returned no usable score for this criterion on this "
                    + "run. It still counts toward the maximum, so this question's "
                    + "score is understated - this is not a judgement about the "
                    + "question."
                  : verdict?.justification || "No justification recorded."}
            </p>
            {issues.length > 0 && (
              <ul className="qr-criterion-issues">
                {issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};
